/**
 * Field Mapping Configuration
 * Maps database column names to PDF field placeholders
 */

export interface FormFieldMapping {
  plot_owner_name: string;
  layout_name: string;
  mauza: string;
  plot_number: string;
  khasara_number: string;
  area_of_plot: string;
  is_there_construction: string;
  construction_area: string;
  court_case: string;
  mobile_number: string;
  email_id: string;
}

/**
 * CORRECT FIELD MAPPING - Based on actual database columns
 * These are the snake_case column names from your database
 */
export const DEFAULT_FIELD_MAPPING: FormFieldMapping = {
  plot_owner_name: "plot_owner_s_name",           // Maps to: Arpita R Vishwakarma
  layout_name: "unauthorized_layout_name",         // Maps to: Beltarodi
  mauza: "mauja",                                  // Maps to: Pardi
  plot_number: "plot_no",                          // Maps to: 45
  khasara_number: "khasara_no",                    // Maps to: 33
  area_of_plot: "area_of_plot",                    // Maps to: 500
  is_there_construction: "is_there_a_construction", // Maps to: false
  construction_area: "construction_area",          // Maps to: 500
  court_case: "is_there_a_court_case_going_on_regarding_the_plot", // Maps to: false
  mobile_number: "mobile_no",                      // Maps to: 7888898654
  email_id: "email_id",                            // Maps to: admin@gmail.com
};

/**
 * Extracts and maps field values from raw application data
 */
export function extractFieldValues(rawData: Record<string, any>): Record<string, string> {
  const mapping = DEFAULT_FIELD_MAPPING;
  const extracted: Record<string, string> = {};

  // Map each field using the defined mapping
  Object.keys(mapping).forEach((targetField) => {
    const sourceField = mapping[targetField as keyof FormFieldMapping];
    const value = getFieldValue(rawData, sourceField);
    extracted[targetField] = value;
  });

  return extracted;
}

/**
 * Gets a field value from the data object, handling various data types
 */
export function getFieldValue(data: Record<string, any>, fieldKey: string): string {
  let value = data[fieldKey];

  if ((value === null || value === undefined || value === "") && fieldKey === "mauja") {
    value = data.village_mouje_name || data.mauja;
  }

  // Handle null/undefined or NaN
  if (value === null || value === undefined || Number.isNaN(value) || String(value) === "NaN") {
    return "";
  }

  if (fieldKey === "mauja" || fieldKey === "village_mouje_name") {
    const valStr = String(value).trim();
    if (valStr === "1" || valStr.toLowerCase() === "kharbi") return "Kharbi";
    if (valStr === "2" || valStr.toLowerCase() === "bahadura") return "Bahadura";
    if (valStr === "3" || valStr.toLowerCase() === "gonhi") return "Gonhi";
    return valStr;
  }

  // Handle boolean values - convert to Marathi Yes/No
  if (typeof value === "boolean") {
    return value ? "होय" : "नाही";
  }

  // Handle objects (nested data)
  if (typeof value === "object" && !Array.isArray(value)) {
    // If object has a 'value' property, use that
    if (value.value !== undefined) {
      return String(value.value);
    }
    // If object has a 'name' property, use that
    if (value.name !== undefined) {
      return String(value.name);
    }
    // Otherwise return empty
    return "";
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join(", ");
  }

  // Handle numbers and strings
  return String(value);
}
